/**
 * sl-m82f.4.1: Workspace AI Usage Analytics Dashboard.
 * sl-4wjh: Extended with 3 context tabs (Workspace/User/Workflow) and
 * new feature tabs (ImageGen, Transcription, Websearch).
 *
 * Layout:
 *   1. Period selector
 *   2. Context tab bar (Workspace / User / Workflow)
 *   3. Feature tab bar (filtered by context + ServerFeature gating)
 *   4. Active tab content
 */
import { SettingWrapper } from '@affine/component/setting-components';
import { Menu, MenuItem, MenuTrigger } from '@affine/component/ui/menu';
import { ServerService } from '@affine/core/modules/cloud';
import { WorkspacePermissionService } from '@affine/core/modules/permissions';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { ServerFeature } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { DoneIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useEffect, useMemo, useState } from 'react';

import { TAB_CONFIGS } from './analytics/constants';
import {
  controlBar,
  dashboardContainer,
  errorStyle,
  loadingStyle,
  periodMenuContentStyle,
  periodMenuDetailStyle,
  periodMenuItemStyle,
  periodMenuLabelStyle,
  periodMenuTitleStyle,
  periodMenuTriggerStyle,
  periodTriggerLabelStyle,
  tabActive,
  tabBar,
  tabBase,
} from './analytics/styles';
import { AiChatTab } from './analytics/tabs/ai-chat-tab';
import { AiMemoryTab } from './analytics/tabs/ai-memory-tab';
import { BusinessIntelligenceTab } from './analytics/tabs/business-intelligence-tab';
import { ImageGenTab } from './analytics/tabs/image-gen-tab';
import { IntelligentSearchTab } from './analytics/tabs/intelligent-search-tab';
import { TranscriptionTab } from './analytics/tabs/transcription-tab';
import { WebsearchTab } from './analytics/tabs/websearch-tab';
import { WorkflowsTab } from './analytics/tabs/workflows-tab';
import type {
  AnalyticsPeriodRange,
  ContextType,
  FeatureTabKey,
  TabConfig,
} from './analytics/types';
import { useFeatureTotals } from './use-feature-totals';

// ─── Period selector ────────────────────────────────────────────────────────

interface PeriodOption {
  key: string;
  label: string;
  title: string;
  detail?: string;
  range?: AnalyticsPeriodRange;
}

const CURRENT_PERIOD: PeriodOption = {
  key: 'current',
  label: 'Aktuell',
  title: 'Aktuelle Abrechnungsperiode',
};

function daysInUtcMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

function addUtcMonths(date: Date, delta: number): Date {
  const targetMonth = date.getUTCMonth() + delta;
  const targetYear = date.getUTCFullYear() + Math.floor(targetMonth / 12);
  const normalizedMonth = ((targetMonth % 12) + 12) % 12;
  const day = Math.min(
    date.getUTCDate(),
    daysInUtcMonth(targetYear, normalizedMonth)
  );
  return new Date(
    Date.UTC(
      targetYear,
      normalizedMonth,
      day,
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds()
    )
  );
}

function formatDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatMonthLabel(date: Date): string {
  return date.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
}

function formatRange(sinceIso?: string, untilIso?: string): string {
  if (!sinceIso || !untilIso) return '';
  return `${formatDate(new Date(sinceIso))} - ${formatDate(new Date(untilIso))}`;
}

function rangeOption(
  key: string,
  label: string,
  title: string,
  start: Date,
  end: Date
): PeriodOption {
  const range = {
    key,
    label,
    since: start.toISOString(),
    until: end.toISOString(),
  };
  return {
    key,
    label,
    title,
    detail: formatRange(range.since, range.until),
    range,
  };
}

function buildPeriodOptions(
  anchor: AnalyticsPeriodRange | null
): PeriodOption[] {
  if (!anchor?.since || !anchor.until) {
    return [CURRENT_PERIOD];
  }

  const currentStart = new Date(anchor.since);
  const currentEnd = new Date(anchor.until);
  const options: PeriodOption[] = [
    {
      ...CURRENT_PERIOD,
      label: formatMonthLabel(currentStart),
      detail: formatRange(anchor.since, anchor.until),
    },
  ];

  for (let offset = 1; offset < 12; offset++) {
    const start = addUtcMonths(currentStart, -offset);
    const end = addUtcMonths(currentEnd, -offset);
    options.push(
      rangeOption(
        `billing-period-${offset}`,
        formatMonthLabel(start),
        offset === 1
          ? 'Letzte Abrechnungsperiode'
          : `Vor ${offset} Abrechnungsperioden`,
        start,
        end
      )
    );
  }

  for (let quarter = 1; quarter <= 4; quarter++) {
    const end = addUtcMonths(currentStart, -(quarter - 1) * 3);
    const start = addUtcMonths(currentStart, -quarter * 3);
    options.push(
      rangeOption(
        `billing-quarter-${quarter}`,
        `${formatMonthLabel(start)} - ${formatMonthLabel(addUtcMonths(end, -1))}`,
        quarter === 1
          ? 'Letztes vollständiges Quartal'
          : `Vor ${quarter} Quartalen`,
        start,
        end
      )
    );
  }

  return options;
}

function periodTitle(option: PeriodOption | undefined): string {
  if (!option) return CURRENT_PERIOD.label;
  return option.detail ? `${option.label} (${option.detail})` : option.label;
}

function PeriodSelector({
  options,
  selectedKey,
  onChange,
}: {
  options: PeriodOption[];
  selectedKey: string;
  onChange: (key: string) => void;
}) {
  const selected = options.find(option => option.key === selectedKey);
  return (
    <Menu
      items={
        <>
          {options.map(option => {
            const isSelected = option.key === selectedKey;
            return (
              <MenuItem key={option.key} onSelect={() => onChange(option.key)}>
                <div style={periodMenuItemStyle}>
                  <div style={periodMenuLabelStyle}>
                    <span style={periodMenuTitleStyle}>{option.label}</span>
                    {option.detail && (
                      <span style={periodMenuDetailStyle}>{option.detail}</span>
                    )}
                  </div>
                  {isSelected && <DoneIcon fontSize={16} />}
                </div>
              </MenuItem>
            );
          })}
        </>
      }
      contentOptions={{
        align: 'end',
        style: periodMenuContentStyle,
      }}
    >
      <MenuTrigger style={periodMenuTriggerStyle}>
        <span style={periodTriggerLabelStyle}>
          <span style={periodMenuTitleStyle}>
            {selected?.label ?? CURRENT_PERIOD.label}
          </span>
          {selected?.detail && (
            <span style={periodMenuDetailStyle}>{selected.detail}</span>
          )}
        </span>
      </MenuTrigger>
    </Menu>
  );
}

// ─── Tab gating ─────────────────────────────────────────────────────────────

function isTabEnabled(tab: TabConfig, features: ServerFeature[]): boolean {
  if (!tab.requiredFeature) return true;
  return features.includes(ServerFeature[tab.requiredFeature]);
}

// ─── Context tabs ───────────────────────────────────────────────────────────

const I18N = 'com.streamlain.settings.workspace.ai.analytics' as const;

interface ContextTabDef {
  key: ContextType;
  labelKey: string;
  defaultLabel: string;
}

const contextDescriptionStyle = {
  padding: '6px 2px 10px',
  fontSize: 12,
  lineHeight: '18px',
  color: 'var(--streamlain-v2-text-secondary)',
} as const;

function contextDescription(
  context: ContextType,
  t: ReturnType<typeof useI18n>
): string {
  const fallback: Record<ContextType, string> = {
    workspace:
      'Background costs that cannot be attributed to a single member or workflow — document embeddings for search, knowledge graph, auto-memory summaries.',
    user: 'AI and workflow credits per member — interactive chat, copilot, image generation, transcription, web search, and manual workflow runs.',
    workflow:
      'AI credits used by workflow steps. Workflow-minute billing is shown in Workflows.',
  };
  return t[`${I18N}.context.${context}.subtitle`]?.() ?? fallback[context];
}

const CONTEXT_TABS: readonly ContextTabDef[] = [
  {
    key: 'workspace',
    labelKey: `${I18N}.context.workspace`,
    defaultLabel: 'Workspace',
  },
  {
    key: 'user',
    labelKey: `${I18N}.context.user`,
    defaultLabel: 'User',
  },
  {
    key: 'workflow',
    labelKey: `${I18N}.context.workflow`,
    defaultLabel: 'AI in Workflows',
  },
] as const;

// ─── Component ──────────────────────────────────────────────────────────────

export interface UsageAnalyticsProps {
  bare?: boolean;
  /** Show only this specific tab (hides both tab bars). */
  onlyTab?: FeatureTabKey;
  /** Exclude specific tabs from the tab bar. */
  excludeTabs?: FeatureTabKey[];
  /** Override the SettingWrapper title (only when bare=false). */
  title?: string;
  /** Emits the concrete selected billing range label to parent wrappers. */
  onPeriodLabelChange?: (label: string) => void;
}

export const UsageAnalytics = ({
  bare = false,
  onlyTab,
  excludeTabs,
  title: titleOverride,
  onPeriodLabelChange,
}: UsageAnalyticsProps) => {
  const t = useI18n();
  const workspaceId = useService(WorkspaceService).workspace.id;

  const permissionService = useService(WorkspacePermissionService).permission;
  const isOwner = useLiveData(permissionService.isOwner$);
  const isAdmin = useLiveData(permissionService.isAdmin$);
  const canView = Boolean(isOwner || isAdmin);

  const serverService = useService(ServerService);
  const serverConfig = useLiveData(serverService.server.config$);
  const serverFeatures = useMemo(
    () => serverConfig?.features ?? [],
    [serverConfig]
  );

  const [period, setPeriod] = useState<string>(CURRENT_PERIOD.key);
  const [billingAnchor, setBillingAnchor] =
    useState<AnalyticsPeriodRange | null>(null);
  const [context, setContext] = useState<ContextType>('user');
  const [activeTabKey, setActiveTabKey] = useState<FeatureTabKey>(
    onlyTab ?? 'ai_chat'
  );

  const periodOptions = useMemo(
    () => buildPeriodOptions(billingAnchor),
    [billingAnchor]
  );
  const selectedPeriod = useMemo(
    () =>
      periodOptions.find(option => option.key === period) ?? periodOptions[0],
    [periodOptions, period]
  );
  const featureTotalsPeriod =
    selectedPeriod?.key === CURRENT_PERIOD.key
      ? CURRENT_PERIOD.key
      : (selectedPeriod?.range ?? CURRENT_PERIOD.key);

  const {
    data: featureTotals,
    isLoading,
    error,
  } = useFeatureTotals(workspaceId, featureTotalsPeriod);

  useEffect(() => {
    if (period !== CURRENT_PERIOD.key || !featureTotals) return;
    setBillingAnchor({
      key: CURRENT_PERIOD.key,
      since: featureTotals.periodStart,
      until: featureTotals.periodEnd,
    });
  }, [featureTotals, period]);

  const selectedPeriodTitle = useMemo(() => {
    if (featureTotals) {
      return periodTitle({
        ...(selectedPeriod ?? CURRENT_PERIOD),
        detail: formatRange(featureTotals.periodStart, featureTotals.periodEnd),
      });
    }
    return periodTitle(selectedPeriod);
  }, [featureTotals, selectedPeriod]);

  useEffect(() => {
    onPeriodLabelChange?.(selectedPeriodTitle);
  }, [onPeriodLabelChange, selectedPeriodTitle]);

  // Tab visibility rules:
  // 1. `onlyTab` bypasses context + visibility gates entirely — callers use
  //    it to render one specific tab on a page that has its own wrapper
  //    (e.g. workspace:workflows shows just the workflows tab).
  // 2. Otherwise: must match the active context.
  // 3. If `requiredFeature` is set and the capability is NOT provisioned AND
  //    credits === 0 → hide (historical usage keeps the tab visible so the
  //    user can audit past spend even after a capability is turned off).
  // 4. If `hideIfZero` is set (image-gen, transcription, websearch) AND
  //    credits === 0 → hide (don't clutter the UI with never-used secondary
  //    features).
  const visibleTabs = useMemo(() => {
    if (onlyTab) {
      return TAB_CONFIGS.filter(tab => tab.key === onlyTab);
    }
    let tabs = TAB_CONFIGS.filter(tab => {
      if (!tab.contexts.includes(context)) return false;
      const provisioned = isTabEnabled(tab, serverFeatures);
      const credits = featureTotals?.features?.[tab.featureKey] ?? 0;
      if (credits === 0) {
        if (tab.requiredFeature && !provisioned) return false;
        if (tab.hideIfZero) return false;
      }
      return true;
    });
    if (excludeTabs?.length)
      tabs = tabs.filter(tab => !excludeTabs.includes(tab.key));
    return tabs;
  }, [serverFeatures, context, onlyTab, excludeTabs, featureTotals]);

  // Clamp the active tab when context changes or gating changes.
  const effectiveActiveTab: FeatureTabKey = useMemo(() => {
    if (visibleTabs.some(tab => tab.key === activeTabKey)) return activeTabKey;
    return visibleTabs[0]?.key ?? 'ai_chat';
  }, [visibleTabs, activeTabKey]);

  if (!canView) return null;

  const activeTab = visibleTabs.find(tab => tab.key === effectiveActiveTab);
  const showContextTabs = !onlyTab;
  const showFeatureTabs = !onlyTab && visibleTabs.length > 1;
  const analyticsPeriod = featureTotals
    ? {
        key: selectedPeriod?.key ?? period,
        label: selectedPeriod?.label,
        since: featureTotals.periodStart,
        until: featureTotals.periodEnd,
      }
    : featureTotalsPeriod;

  const dashboardContent = (
    <div style={dashboardContainer}>
      <div style={controlBar}>
        <PeriodSelector
          options={periodOptions}
          selectedKey={selectedPeriod?.key ?? CURRENT_PERIOD.key}
          onChange={setPeriod}
        />
        {showContextTabs && (
          <div style={tabBar} role="tablist" aria-label="Context">
            {CONTEXT_TABS.map(ct => {
              const label = t[ct.labelKey]?.() ?? ct.defaultLabel;
              const isActive = ct.key === context;
              return (
                <button
                  key={ct.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  data-testid={`analytics-context-${ct.key}`}
                  style={isActive ? tabActive : tabBase}
                  onClick={() => setContext(ct.key)}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {showContextTabs && (
        <div style={contextDescriptionStyle}>
          {contextDescription(context, t)}
        </div>
      )}

      {isLoading && (
        <div style={loadingStyle}>
          {t[`${I18N}.loading`]?.() ?? 'Loading analytics\u2026'}
        </div>
      )}
      {error && <div style={errorStyle}>{error}</div>}

      {!isLoading && !error && featureTotals && (
        <>
          {showFeatureTabs && (
            <div style={tabBar} role="tablist">
              {visibleTabs.map(tab => {
                const label = t[tab.labelKey]?.() ?? tab.defaultLabel;
                const isActive = tab.key === effectiveActiveTab;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    data-testid={`analytics-tab-${tab.key}`}
                    style={isActive ? tabActive : tabBase}
                    onClick={() => setActiveTabKey(tab.key)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}

          {activeTab?.key === 'ai_chat' && (
            <AiChatTab
              workspaceId={workspaceId}
              period={analyticsPeriod}
              context={context}
            />
          )}
          {activeTab?.key === 'intelligent-search' && (
            <IntelligentSearchTab
              workspaceId={workspaceId}
              period={analyticsPeriod}
              featureTotals={featureTotals}
            />
          )}
          {activeTab?.key === 'ai-memory' && (
            <AiMemoryTab
              workspaceId={workspaceId}
              period={analyticsPeriod}
              featureTotals={featureTotals}
            />
          )}
          {activeTab?.key === 'business-intelligence' && (
            <BusinessIntelligenceTab
              workspaceId={workspaceId}
              period={analyticsPeriod}
              featureTotals={featureTotals}
            />
          )}
          {activeTab?.key === 'image-gen' && (
            <ImageGenTab
              workspaceId={workspaceId}
              period={analyticsPeriod}
              context={context}
            />
          )}
          {activeTab?.key === 'transcription' && (
            <TranscriptionTab
              workspaceId={workspaceId}
              period={analyticsPeriod}
              context={context}
            />
          )}
          {activeTab?.key === 'websearch' && (
            <WebsearchTab
              workspaceId={workspaceId}
              period={analyticsPeriod}
              context={context}
            />
          )}
          {activeTab?.key === 'workflows' && (
            <WorkflowsTab workspaceId={workspaceId} period={analyticsPeriod} />
          )}
        </>
      )}
    </div>
  );

  if (bare) return dashboardContent;

  return (
    <SettingWrapper
      title={titleOverride ?? t[`${I18N}.title`]?.() ?? 'Usage Analytics'}
    >
      {dashboardContent}
    </SettingWrapper>
  );
};
